import { NavLink } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useQuery } from '@/api/query'

/**
 * The budget meter in the sidebar foot — `shell#settings`' own `sc-side__foot`.
 *
 * **Renders nothing until there is a cap**, which is most tenants on most days:
 * a meter against no ceiling would be a bar that could never move, and the
 * design draws this for an organisation that has set one. The loading and error
 * states are the same absence for the same reason — a skeleton in the sidebar
 * of every page is a flicker on every navigation, and a sidebar is not where a
 * failed request should be reported.
 *
 * Reading is `tenant:read`, so this is drawn for whoever is signed in, exactly
 * as S-D3 draws it. `app/v1/usage.py` states why the read is not `billing:read`.
 */
export function BudgetMeter(): JSX.Element | null {
  const budget = useQuery((signal) => call(() => api.GET('/api/v1/usage/budget', { signal })), [])

  if (budget.state.status !== 'ready') return null
  const { budget: cap, spend } = budget.state.data
  if (cap.effective_cap_minor === null || cap.effective_cap_minor === undefined) return null

  const percent = Math.min(100, Math.max(0, spend.percent ?? 0))
  return (
    <NavLink className="ar-panel app-budget" to="/billing/usage">
      <div className="ar-row" style={{ justifyContent: 'space-between' }}>
        <span className="ar-muted">Budget</span>
        <span className="ar-mono">{percent}%</span>
      </div>
      <div
        className="ar-meter"
        role="meter"
        aria-label="Budget used this month"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="ar-meter__fill" style={{ width: `${String(percent)}%` }} />
      </div>
      <div className="ar-subtle">
        {spend.spend_display} of {cap.effective_display} this month
      </div>
    </NavLink>
  )
}
